import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { motion } from "framer-motion";

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>;
  isLoading: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading }) => {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fonction pour redimensionner le textarea de façon plus stable
  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Déterminer si nous sommes sur mobile
    const isMobile = window.innerWidth < 768;

    // Hauteur de base en fonction de l'appareil
    const baseHeight = isMobile ? "40px" : "48px";
    // Limite de hauteur maximale
    const maxHeight = isMobile ? 120 : 160;

    // Réinitialiser la hauteur pour recalculer
    textarea.style.height = baseHeight;

    // Calculer la nouvelle hauteur, mais ne pas dépasser maxHeight
    const newHeight = Math.min(maxHeight, textarea.scrollHeight);
    textarea.style.height = `${newHeight}px`;
  };

  // Redimensionne seulement lors de l'ajout/suppression de lignes
  useEffect(() => {
    // Utiliser un délai pour éviter des mises à jour trop fréquentes
    const timeoutId = setTimeout(() => {
      resizeTextarea();
    }, 10);

    return () => clearTimeout(timeoutId);
  }, [input]);

  // Redimensionne également lors du focus/blur
  useEffect(() => {
    if (isFocused) {
      resizeTextarea();
    }
  }, [isFocused]);

  // Redimensionne lors du changement de taille de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      resizeTextarea();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    await onSendMessage(input);
    setInput("");

    // Réinitialiser la hauteur après envoi
    if (textareaRef.current) {
      // Déterminer si nous sommes sur mobile
      const isMobile = window.innerWidth < 768;
      textareaRef.current.style.height = isMobile ? "40px" : "48px";
      textareaRef.current.focus();
    }
  };

  return (
    <motion.div
      ref={containerRef}
      className="border-t border-base-200 p-2 sm:p-3 md:p-4 bg-base-100 sticky bottom-0"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <form onSubmit={handleSubmit} className="flex gap-2 items-end">
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Posez votre question..."
            className="textarea textarea-bordered w-full resize-none text-sm md:text-base p-3 pr-10 min-h-[40px] md:min-h-[48px] focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          {input.length > 0 && (
            <div className="absolute right-2 bottom-2 text-xs text-gray-500 bg-base-100 px-1 rounded-sm opacity-70">
              {input.length}
            </div>
          )}
        </div>
        <motion.button
          type="submit"
          className={`btn h-9 md:h-10 min-h-0 w-9 md:w-10 p-0 flex items-center justify-center ${
            isLoading || !input.trim()
              ? "btn-disabled bg-gray-300"
              : "btn-primary bg-purple-600 hover:bg-purple-700"
          } rounded-full`}
          disabled={isLoading || !input.trim()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
          ) : (
            <Send className="h-4 w-4 md:h-5 md:w-5" />
          )}
        </motion.button>
      </form>

      <div className="text-xs text-gray-500 mt-1 text-center hidden sm:block">
        <span className="opacity-70">Shift+Entrée pour sauter une ligne</span>
      </div>
    </motion.div>
  );
};

export default ChatInput;
